import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router';
import { RequireAuth } from '../components/require-auth';
import { ForgotPasswordPage } from '../pages/auth/forgot-password-page';
import { LoginPage } from '../pages/auth/login-page';
import { RegisterPage } from '../pages/auth/register-page';
import { ResetPasswordPage } from '../pages/auth/reset-password-page';
import { SecondFactorPage } from '../pages/auth/second-factor-page';
import { ConfirmEmailPage } from '../pages/auth/token-action-page';
import { VerifyEmailPage } from '../pages/auth/verify-email-page';
import { HomeScreen } from '../pages/app/home-screen';
import { AcceptInvitationPage } from '../pages/invitations/accept-invitation-page';
import { GuestShell } from '../pages/landing/guest-shell';
import { NotFoundPage } from '../pages/not-found-page';
import { ProfilePage } from '../pages/profile/profile-page';
import { SecurityPage } from '../pages/profile/security-page';
import { SettingsLayout } from '../pages/settings/settings-layout';
import { AuditPage } from '../pages/workspaces/audit-page';
import { InvitationsPage } from '../pages/workspaces/invitations-page';
import { MembersPage } from '../pages/workspaces/members-page';
import { OverviewPage } from '../pages/workspaces/overview-page';
import { WorkspacesPage } from '../pages/workspaces/workspaces-page';
import { RealtimeProvider } from '../realtime/realtime-provider';
import { paths } from './paths';
import { WorkspaceRoute } from './workspace-route';

/** Закрытая часть: сессия обязательна, realtime — одно подключение на вкладку. */
function AuthenticatedApp() {
  return (
    <RealtimeProvider>
      <Outlet />
    </RealtimeProvider>
  );
}

/**
 * Старый адрес профиля зашит в редиректы Google (google.controller): привязка Google и её ошибки
 * — на «Безопасность», с query.
 */
function ProfileRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const target = params.has('google') || params.has('error') ? paths.security : paths.profile;
  return <Navigate to={`${target}${search}`} replace />;
}

export const routes = [
  {
    // Лендинг; гостевые формы — окном поверх него (docs/saas/screens/лендинг.md).
    element: <GuestShell />,
    children: [
      { index: true, element: null },
      { path: 'login', element: <LoginPage /> },
      { path: 'login/second-factor', element: <SecondFactorPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'verify-email', element: <VerifyEmailPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'confirm-email', element: <ConfirmEmailPage /> },
      { path: 'invitations/accept', element: <AcceptInvitationPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AuthenticatedApp />,
        children: [
          { path: 'profile', element: <ProfileRedirect /> },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { path: 'profile', element: <ProfilePage /> },
              { path: 'security', element: <SecurityPage /> },
              { path: 'workspaces', element: <WorkspacesPage /> },
            ],
          },
          {
            path: 'w/:workspaceId',
            element: <WorkspaceRoute />,
            children: [
              { index: true, element: <HomeScreen /> },
              {
                path: 'd/:diagramId',
                // Холст (библиотека схемы, ELK) — отдельным чанком: домашнему экрану и настройкам не нужен.
                lazy: () =>
                  import('../pages/app/diagram-screen').then((module) => ({
                    Component: module.DiagramScreen,
                  })),
              },
              {
                path: 'settings',
                element: <SettingsLayout />,
                children: [
                  { index: true, element: <OverviewPage /> },
                  { path: 'members', element: <MembersPage /> },
                  { path: 'invitations', element: <InvitationsPage /> },
                  { path: 'audit', element: <AuditPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

// Адреса страниц входа и приглашения зашиты в письма и редиректы API — см. paths.ts.
export const router = createBrowserRouter(routes);
